import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useConversationAnalysis } from "@/hooks/agent/useConversationAnalysis";
import { SetupPhase, SetupMode, useGenieSetupReducer } from "@/hooks/agent/useGenieSetupReducer";
import { SetupRecoveryService } from "@/services/agent/recovery";
import { SetupFunnelService } from "@/services/analytics/SetupFunnelService";
import { VisionAnalysisService } from "@/services/agent/vision";
import { AgentMessage, SetupAnalysisResult } from "@/types/agent/setup";
import { DEFAULT_STATUS_MESSAGES } from "@/types/voice/conversation-state";
import { useGenieConversationBase } from "./useGenieConversationBase";

const getRandomMessage = (messages: string[]): string => {
  return messages[Math.floor(Math.random() * messages.length)];
};

export const useGenieSetup = () => {
  const [state, dispatch] = useGenieSetupReducer();
  const { analyzeConversation, createGenieFromAnalysis } = useConversationAnalysis();

  const {
    speaking,
    listening,
    thinking,
    connected,
    isConnecting,
    setupAgent,
    handleConversationToggle: baseHandleConversationToggle,
    resetConversation: baseResetConversation,
    voiceAgent,
  } = useGenieConversationBase({
    onMessageReceived: (message) => {
      console.log("Dispatching ADD_MESSAGE:", message);
      dispatch({ type: "ADD_MESSAGE", payload: message });
    },
    onConversationEnd: async (conversationId) => {
      dispatch({ type: "SET_CONVERSATION_ID", payload: conversationId });

      // Always trigger analysis when ending conversation, regardless of message count
      if (state.setup.phase === "conversation") {
        console.log("Starting analysis with conversation ID:", conversationId);
        await handleAnalyzeConversation(conversationId);
      } else {
        console.log("Not in conversation phase, current phase:", state.setup.phase);
      }
    },
    mode: "setup",
  });

  const handleConversationToggle = useCallback(async () => {
    if (state.setup.error) {
      dispatch({ type: "SET_ERROR", payload: null });
    }
    return await baseHandleConversationToggle();
  }, [baseHandleConversationToggle, state.setup.error, dispatch]);

  const handleAnalyzeConversation = useCallback(
    async (conversationId?: string | null) => {
      console.log("handleAnalyzeConversation called with conversation ID:", conversationId);

      dispatch({ type: "SET_PHASE", payload: "analysis" });
      dispatch({ type: "SET_IS_ANALYZING", payload: true });
      SetupFunnelService.phaseReached(conversationId ?? null, "analysis");
      toast.info("Analyzing conversation to extract genie parameters...");

      try {
        const analysis = await analyzeConversation(state.conversation.messages, conversationId);

        dispatch({ type: "SET_ANALYSIS_RESULT", payload: analysis });
        dispatch({ type: "SET_PHASE", payload: "confirmation" });
        SetupFunnelService.phaseReached(conversationId ?? null, "confirmation");
        toast.success("Analysis complete! Please review the extracted parameters.");
      } catch (error) {
        console.error("Analysis failed:", error);

        const errorMessage = error instanceof Error ? error.message : "Failed to analyze conversation";
        dispatch({ type: "SET_ERROR", payload: errorMessage });
        SetupFunnelService.phaseFailed(conversationId ?? null, "analysis", errorMessage);

        // Analysis is the one phase in this flow that can REVERSE a customer:
        // it is the only failure path that sends them back to a phase they had
        // already completed. Before doing that, save what they actually said,
        // so a later resume works from a transcript we hold rather than one
        // upstream may have expired.
        void SetupRecoveryService.persistDerivedSetupState(conversationId, {
          transcript: state.conversation.messages,
        });

        toast.error("We could not read that conversation back just now.", {
          description: "Everything you said is saved. Try again, or resume from My Genies later.",
          action: {
            label: "Try again",
            onClick: () => void handleAnalyzeConversation(conversationId),
          },
          duration: 12000,
        });

        dispatch({ type: "SET_PHASE", payload: "conversation" });
      } finally {
        dispatch({ type: "SET_IS_ANALYZING", payload: false });
      }
    },
    [analyzeConversation, state.conversation.messages, dispatch],
  );

  const handleCreateGenieFromAnalysis = useCallback(
    async (finalAnalysis: SetupAnalysisResult) => {
      dispatch({ type: "SET_IS_CREATING_GENIE", payload: true });

      try {
        const createdAgent = await createGenieFromAnalysis(finalAnalysis);

        // Store the created agent in state
        dispatch({ type: "SET_CREATED_AGENT", payload: createdAgent });
        dispatch({ type: "SET_PHASE", payload: "complete" });
      } catch (error) {
        console.error("Genie creation failed:", error);

        toast.error("Failed to create genie, but don't worry!", {
          description: "Your conversation and analysis are safely saved. You can resume from the confirmation page.",
        });

        // Stay on confirmation page so user can retry or modify settings
        // Don't reset to conversation phase
      } finally {
        dispatch({ type: "SET_IS_CREATING_GENIE", payload: false });
      }
    },
    [createGenieFromAnalysis, dispatch, state.conversation.conversationId],
  );

  const resetSetup = useCallback(() => {
    dispatch({ type: "RESET_SETUP" });
    baseResetConversation();
  }, [dispatch, baseResetConversation]);

  const clearError = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });
  }, [dispatch]);

  const handleOpenAIParametersExtracted = useCallback(
    (parameters: any) => {
      dispatch({ type: "HANDLE_OPENAI_PARAMETERS", payload: parameters });
    },
    [dispatch],
  );

  const handleOpenAIConversationUpdate = useCallback(
    (agentMessages: AgentMessage[]) => {
      dispatch({ type: "HANDLE_OPENAI_CONVERSATION_UPDATE", payload: agentMessages });
    },
    [dispatch],
  );

  const setShowTranscript = useCallback(
    (show: boolean) => {
      dispatch({ type: "SET_SHOW_TRANSCRIPT", payload: show });
    },
    [dispatch],
  );

  const setSetupPhase = useCallback(
    (phase: SetupPhase) => {
      dispatch({ type: "SET_PHASE", payload: phase });
    },
    [dispatch],
  );

  const setSetupMode = useCallback(
    (mode: SetupMode) => {
      dispatch({ type: "SET_SETUP_MODE", payload: mode });
    },
    [dispatch],
  );

  const handleResumeSetup = useCallback(async () => {
    if (!state.conversation.conversationId) {
      toast.error("No conversation ID found to resume from");
      return;
    }

    try {
      dispatch({ type: "SET_IS_RESUMING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });
      toast.info("Resuming setup from conversation transcript...");

      const { transcript, analysis } = await SetupRecoveryService.resumeSetupFromConversation(state.conversation.conversationId);

      dispatch({ type: "SET_MESSAGES", payload: transcript });
      dispatch({ type: "SET_ANALYSIS_RESULT", payload: analysis });
      dispatch({ type: "SET_PHASE", payload: "confirmation" });

      toast.success("Setup resumed successfully! Please review the extracted parameters.");
    } catch (error) {
      console.error("Failed to resume setup:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to resume setup";
      dispatch({ type: "SET_ERROR", payload: errorMessage });

      toast.error("We could not bring that setup back", {
        description:
          "This one was started before we began saving setups as you go, so there is nothing left to restore. New setups are saved automatically.",
      });
    } finally {
      dispatch({ type: "SET_IS_RESUMING", payload: false });
    }
  }, [state.conversation.conversationId, dispatch]);

  const handleAnalyzeImage = useCallback(
    async (imageDataUrl: string) => {
      console.log("handleAnalyzeImage called");

      dispatch({ type: "SET_PHASE", payload: "analysis" });
      dispatch({ type: "SET_IS_ANALYZING", payload: true });
      toast.info("Analyzing image to extract genie parameters...");

      try {
        const visionService = new VisionAnalysisService();
        const analysis = await visionService.analyzeImage(imageDataUrl);

        dispatch({ type: "SET_ANALYSIS_RESULT", payload: analysis });
        dispatch({ type: "SET_PHASE", payload: "image-results" });
        toast.success("Analysis complete! Review the extracted information.");
      } catch (error) {
        console.error("Image analysis failed:", error);

        const errorMessage = error instanceof Error ? error.message : "Failed to analyze image";
        dispatch({ type: "SET_ERROR", payload: errorMessage });

        toast.error("Image analysis failed. Please try again or use voice setup.", {
          description: "You can upload a different image or switch to voice conversation mode.",
        });

        dispatch({ type: "SET_PHASE", payload: "image-upload" });
      } finally {
        dispatch({ type: "SET_IS_ANALYZING", payload: false });
      }
    },
    [dispatch],
  );

  const statusMessage = useMemo(() => {
    if (state.setup.isResuming) return "Resuming setup from conversation...";
    if (state.setup.phase === "analysis") return "Analyzing...";
    if (state.setup.phase === "image-upload") return "Upload a business image to get started";
    if (state.setup.phase === "confirmation") return "Review and confirm parameters";
    if (state.setup.phase === "complete") return "Genie created successfully!";
    if (!state.setup.setupMode) return "Choose how to set up your Genie";
    if (state.setup.setupMode === "chat") return "Chat with our guide about your genie";
    if (state.setup.error && !connected) return "Setup encountered an error. Click Resume to continue.";
    if (isConnecting) return getRandomMessage(DEFAULT_STATUS_MESSAGES.connecting);
    if (!connected) return "Click to start setting up your Genie";
    if (speaking) return getRandomMessage(DEFAULT_STATUS_MESSAGES.speaking);
    if (thinking) return getRandomMessage(DEFAULT_STATUS_MESSAGES.thinking);
    if (listening) return getRandomMessage(DEFAULT_STATUS_MESSAGES.listening);
    return getRandomMessage(DEFAULT_STATUS_MESSAGES.ready);
  }, [state.setup.phase, state.setup.setupMode, state.setup.isResuming, state.setup.error, isConnecting, connected, speaking, listening, thinking]);

  const getStatusMessage = useCallback(() => statusMessage, [statusMessage]);

  return {
    // Voice agent state
    speaking,
    listening,
    thinking,
    connected,
    isConnecting,

    // Conversation state
    showTranscript: state.conversation.showTranscript,
    messages: state.conversation.messages,

    // Setup state
    setupPhase: state.setup.phase,
    setupMode: state.setup.setupMode,
    analysisResult: state.setup.analysisResult,
    isCreatingGenie: state.setup.isCreatingGenie,
    isAnalyzing: state.setup.isAnalyzing,
    error: state.setup.error,
    isResuming: state.setup.isResuming,
    createdAgent: state.setup.createdAgent,
    setupAgent,

    // Actions
    setShowTranscript,
    setSetupPhase,
    setSetupMode,
    handleConversationToggle,
    analyzeConversation: handleAnalyzeConversation,
    analyzeImage: handleAnalyzeImage,
    createGenieFromAnalysis: handleCreateGenieFromAnalysis,
    resumeSetup: handleResumeSetup,
    clearError,
    resetSetup,
    handleOpenAIParametersExtracted,
    handleOpenAIConversationUpdate,
    getStatusMessage,
    getService: () => voiceAgent.getService(),
    dispatch,
    voiceAgent,
  };
};
